import { IS_DE_FR } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { isInZeloolApp } from '@/utils/tools';
import logger from '@/utils/client-logger';
import { ApplovinProvider } from './providers/applovinProvider';
import { AwinProvider } from './providers/awinProvider';
import { AwsWafProvider } from './providers/awsWafProvider';
import { BingProvider } from './providers/bingProvider';
import { ConsentManagerProvider } from './providers/consentManagerProvider';
import { CriteoProvider } from './providers/criteoProvider';
import { EmarsysProvider } from './providers/emarsysProvider';
import { FacebookProvider } from './providers/facebookProvider';
import { Ga4Provider } from './providers/ga4Provider';
import { GoogleBadgeProvider } from './providers/googleBadgeProvider';
import { GoogleTranslateProvider } from './providers/googleTranslateProvider';
import { ImpactRadiusProvider } from './providers/impactProvider';
import { OneTrustProvider } from './providers/oneTrustProvider';
import { OpenAiPixelProvider } from './providers/openAiPixelProvider';
import { PepperjamProvider } from './providers/pepperjamProvider';
import { RtbHouseProvider } from './providers/rtbHouseProvider';
import { SensorsProvider } from './providers/sensorProvider';
import { SnapchatProvider } from './providers/snapchatProvider';
import { SnapchatConversionsProvider } from './providers/snapchatConversionsProvider';
import { TiktokProvider } from './providers/tiktokProvider';
import { TwitterProvider } from './providers/twitterProvider';
import { createZeeloolSnowplowDataAdapters } from './integrations/snowplow-sdk/adapters';
import { configureZeeloolSnowplowClient } from './integrations/snowplow-sdk/client';
import { resolveZeeloolSnowplowSdkConfig } from './integrations/snowplow-sdk/config';
import { SnowplowSdkProvider } from './integrations/snowplow-sdk/provider';
import { ReportingCenter } from './reportingCenter';
import {
  SgtmPixelConfig,
  TrackingConfig,
  getTrackingEnabled,
  parseSgtmPixelConfig,
  resolveProviderEnabled,
} from './trackingConfig';
import { generateEventId } from './utils/eventIdGenerator';
import { ProviderType } from './types';
import { configureBrowserAnalytics } from './reliability/browserAnalytics';

// Export the types and main class
export * from './types';
export { ReportingCenter };
export type { TrackingConfig, SgtmPixelConfig };
export { getZeeloolSnowplowClient } from './integrations/snowplow-sdk/client';

/** 页面生命周期内共享的 Reporting 初始化 Promise，防止 Strict Mode 或重挂载重复注册 Provider。 */
let reportingInitializationPromise: Promise<ReportingCenter> | null = null;

/**
 * 初始化全局 Reporting；并发调用与完成后的重复调用均复用首次初始化结果。
 */
export function initializeReporting(
  deviceType: EPlatform,
  trackingConfig?: TrackingConfig,
  sgtmPixelConfig?: SgtmPixelConfig | null,
): Promise<ReportingCenter> {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return Promise.resolve(ReportingCenter.getInstance());
  if (!reportingInitializationPromise) {
    reportingInitializationPromise = initializeReportingOnce(
      deviceType,
      trackingConfig,
      sgtmPixelConfig,
    );
  }

  return reportingInitializationPromise;
}

/**
 * 按首次调用的设备与远程配置注册并初始化全部 Reporting Provider。
 */
async function initializeReportingOnce(
  deviceType: EPlatform,
  trackingConfig?: TrackingConfig,
  sgtmPixelConfig?: SgtmPixelConfig | null,
): Promise<ReportingCenter> {
  const center = ReportingCenter.getInstance();
  const isInZeloolAppFlag = isInZeloolApp();

  // 将 sgtm_pixel_config 转为统一格式，方便 getTrackingEnabled 复用
  const sgtmAsTracking = parseSgtmPixelConfig(sgtmPixelConfig);

  logger.info('[initializeReporting] sgtmPixelConfig =', sgtmPixelConfig ?? 'null');

  // 辅助函数：合并多层配置源，确定最终 isEnabled
  const resolve = (
    providerType: ProviderType,
    envEnabled: boolean,
    runtimeConstraint?: boolean,
  ) => {
    const useSgtmPixelConfig = providerType !== ProviderType.SENSORS;
    const sgtmEnabled = getTrackingEnabled(sgtmAsTracking, providerType);
    const trackingEnabled = getTrackingEnabled(trackingConfig, providerType);
    const result = resolveProviderEnabled({
      runtimeConstraint,
      useSgtmPixelConfig,
      sgtmPixelConfigEnabled: sgtmEnabled,
      trackingConfigEnabled: trackingEnabled,
      envEnabled,
    });

    if (useSgtmPixelConfig && sgtmEnabled !== undefined) {
      const overrideInfo = envEnabled !== result ? `(overrides env=${envEnabled})` : '';
      logger.info(
        `[initializeReporting] ${providerType}: sgtm=${sgtmEnabled} → ${result ? 'enabled' : 'disabled'} ${overrideInfo}`,
      );
    }

    return result;
  };

  // Sensors Provider 只允许 tracking_config / 环境变量控制，不接受 sgtm_pixel_config 控制。
  const sensorsProvider = new SensorsProvider({
    serverUrl: env.NEXT_PUBLIC_SENSORS_SERVER_URL,
    showLog: env.NEXT_PUBLIC_SENSORS_SHOW_LOG,
    platform: deviceType === EPlatform.WEB ? 'Web' : 'Wap',
    platformName:
      deviceType === EPlatform.WEB
        ? (env.NEXT_PUBLIC_SENSORS_PUB_PLATFORM_NAME ?? 'zeeloolWeb')
        : (env.NEXT_PUBLIC_SENSORS_PUB_WAP_PLATFORM_NAME ?? 'zeeloolWap'),
    envName: env.NEXT_PUBLIC_ENV === 'local' ? 'dev' : env.NEXT_PUBLIC_ENV,
    isEnabled: resolve(ProviderType.SENSORS, true),
  });
  center.registerProvider(sensorsProvider);

  // OneTrust Provider，顺序放在前面，尽量在GA4之前就加载好
  center.registerProvider(
    new OneTrustProvider({
      // US 本地和测试环境使用 OneTrust 测试发布，其他环境继续读取生产发布。
      domainScript:
        env.NEXT_PUBLIC_SITE_NAME === 'us' && ['local', 'test'].includes(env.NEXT_PUBLIC_ENV)
          ? 'ad7e7fb7-fa0f-4d1c-8f54-d32280690460-test'
          : 'ad7e7fb7-fa0f-4d1c-8f54-d32280690460',
      isEnabled: resolve(
        ProviderType.ONETRUST,
        env.NEXT_PUBLIC_ENABLE_ONETRUST === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  center.registerProvider(
    new ConsentManagerProvider({
      cdid: env.NEXT_PUBLIC_CONSENT_MANAGER_CDID,
      isEnabled: resolve(
        ProviderType.CONSENT_MANAGER,
        env.NEXT_PUBLIC_ENABLE_CONSENT_MANAGER === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // Emarsys Provider
  center.registerProvider(
    new EmarsysProvider({
      isEnabled: resolve(
        ProviderType.EMARSYS,
        env.NEXT_PUBLIC_ENABLE_EMARSYS === 'true',
        !isInZeloolAppFlag,
      ),
      domainCode: env.NEXT_PUBLIC_EMARSYS_DOMAIN_CODE,
      defaultNotificationTitle: env.NEXT_PUBLIC_EMARSYS_DEFAULT_NOTIFICATION_TITLE,
      defaultNotificationIcon: env.NEXT_PUBLIC_EMARSYS_DEFAULT_NOTIFICATION_ICON,
      applicationServerPublicKey: env.NEXT_PUBLIC_EMARSYS_APPLICATION_SERVER_PUBLIC_KEY,
      scarabJsApiUrl: env.NEXT_PUBLIC_EMARSYS_SCARAB_JS_API_URL,
    }),
  );

  // Impact Radius Provider
  center.registerProvider(
    new ImpactRadiusProvider({
      isEnabled: resolve(
        ProviderType.IMPACT_RADIUS,
        env.NEXT_PUBLIC_ENABLE_IMPACT_RADIUS === 'true',
      ),
    }),
  );

  // Awin Provider
  center.registerProvider(
    new AwinProvider({
      merchantId: env.NEXT_PUBLIC_AWIN_MERCHANT,
      isEnabled: resolve(ProviderType.AWIN, env.NEXT_PUBLIC_ENABLE_AWIN === 'true'),
    }),
  );

  // AppLovin Provider
  center.registerProvider(
    new ApplovinProvider({
      eventKey: env.NEXT_PUBLIC_APPLOVIN_KEY,
      isEnabled: resolve(ProviderType.APPLOVIN, env.NEXT_PUBLIC_ENABLE_APPLOVIN === 'true'),
    }),
  );

  // Bing Provider
  center.registerProvider(
    new BingProvider({
      trackingId: env.NEXT_PUBLIC_BING_ID,
      isEnabled: resolve(
        ProviderType.BING,
        env.NEXT_PUBLIC_ENABLE_BING === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // Criteo Provider
  center.registerProvider(
    new CriteoProvider({
      account: Number(env.NEXT_PUBLIC_CRITEO_ID),
      isEnabled: resolve(ProviderType.CRITEO, env.NEXT_PUBLIC_ENABLE_CRITEO === 'true'),
    }),
  );

  // Facebook Provider
  center.registerProvider(
    new FacebookProvider({
      pixelId: env.NEXT_PUBLIC_FACEBOOK_ID,
      isEnabled: resolve(
        ProviderType.FACEBOOK,
        env.NEXT_PUBLIC_ENABLE_FACEBOOK === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // GA4 Provider
  center.registerProvider(
    new Ga4Provider({
      measurementId: env.NEXT_PUBLIC_GA4_MEASUREMENT_ID,
      adId1: env.NEXT_PUBLIC_GA4_AD_ID1,
      adId2: env.NEXT_PUBLIC_GA4_AD_ID2,
      isEnabled: resolve(ProviderType.GA4, env.NEXT_PUBLIC_ENABLE_GA4 === 'true'),
    }),
  );

  // Pepperjam Provider
  center.registerProvider(
    new PepperjamProvider({
      containerId: env.NEXT_PUBLIC_PEPPERJAM_ID,
      isEnabled: resolve(ProviderType.PEPPERJAM, env.NEXT_PUBLIC_ENABLE_PEPPERJAM === 'true'),
    }),
  );

  // Snapchat Provider
  // 生成共享 PAGE_VIEW eventId，Pixel 侧与 CAPI 侧使用同一值实现去重
  const snapchatPageViewEventId = generateEventId();
  center.registerProvider(
    new SnapchatProvider({
      appKey: env.NEXT_PUBLIC_SNAPCHAT_KEY,
      isEnabled: resolve(ProviderType.SNAPCHAT, env.NEXT_PUBLIC_ENABLE_SNAPCHAT === 'true'),
      pageViewEventId: snapchatPageViewEventId,
    }),
  );

  /**
   * 作为 SnapchatProvider 补充信号
   * Snapchat Conversions Provider
   * 任务诉求: https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/request/49e830ab4fb965ccb3c977a55b
   * 详细参数文档: https://developers.snap.com/api/marketing-api/Conversions-API/Parameters
   */
  center.registerProvider(
    new SnapchatConversionsProvider({
      isEnabled: resolve(
        ProviderType.SNAPCHAT_CONVERSION,
        env.NEXT_PUBLIC_ENABLE_SNAPCHAT_CONVERSION === 'true',
      ),
      platform: deviceType === EPlatform.WEB ? 'WEB' : 'MOBILE_APP',
      pageViewEventId: snapchatPageViewEventId,
    }),
  );

  // TikTok Provider
  center.registerProvider(
    new TiktokProvider({
      pixelId: env.NEXT_PUBLIC_TIKTOK_PIXEL_KEY,
      isEnabled: resolve(
        ProviderType.TIKTOK,
        env.NEXT_PUBLIC_ENABLE_TT_PIXEL === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // OpenAI Ads Measurement Pixel
  center.registerProvider(
    new OpenAiPixelProvider({
      pixelId: env.NEXT_PUBLIC_OPENAI_PIXEL_ID ?? '',
      isEnabled: resolve(
        ProviderType.OPENAI,
        env.NEXT_PUBLIC_ENABLE_OPENAI_PIXEL === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // Twitter Provider
  center.registerProvider(
    new TwitterProvider({
      pixelIds: env.NEXT_PUBLIC_TWITTER_KEYS || [],
      isEnabled: resolve(ProviderType.TWITTER, env.NEXT_PUBLIC_ENABLE_TWITTER === 'true'),
    }),
  );

  // RTB House Provider
  center.registerProvider(
    new RtbHouseProvider({
      trackingId: env.NEXT_PUBLIC_RTB_HOUSE_ID,
      isEnabled: resolve(ProviderType.RTB_HOUSE, env.NEXT_PUBLIC_ENABLE_RTB_HOUSE === 'true'),
      dataCenter: env.NEXT_PUBLIC_RTB_HOUSE_DATA_CENTER,
    }),
  );

  // Google Translate Provider
  center.registerProvider(
    new GoogleTranslateProvider({
      isEnabled: resolve(
        ProviderType.GOOGLE_TRANSLATE,
        env.NEXT_PUBLIC_ENABLE_GOOGLE_TRANSLATE === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // AWS WAF Provider
  center.registerProvider(
    new AwsWafProvider({
      challengeSdkUrl: env.NEXT_PUBLIC_AWS_WAF_CHALLENGE_SDK_URL || '',
      captchaSdkUrl: env.NEXT_PUBLIC_AWS_WAF_CAPTCHA_SDK_URL || '',
      isEnabled: resolve(ProviderType.AWS_WAF, env.NEXT_PUBLIC_ENABLE_AWS_WAF === 'true'),
    }),
  );

  // Google Badge Provider - 只在WEB端显示
  // todo 现在只包含老的谷歌徽章，德法要使用新的谷歌徽章
  center.registerProvider(
    new GoogleBadgeProvider({
      merchantId: env.NEXT_PUBLIC_GOOGLE_BADGE_ID,
      isEnabled: resolve(ProviderType.GOOGLE_BADGE, deviceType === EPlatform.WEB && !IS_DE_FR),
    }),
  );

  // Snowplow 仍在最后注册；Z 集成层只显式解析配置、创建 singleton 并注册 SDK 薄 Provider。
  const snowplowSdkConfig = resolveZeeloolSnowplowSdkConfig({
    enabled: resolve(ProviderType.SNOWPLOW, env.NEXT_PUBLIC_ENABLE_SNOWPLOW === 'true'),
    collectorUrl: env.NEXT_PUBLIC_SNOWPLOW_COLLECTOR_URL,
    appId: env.NEXT_PUBLIC_SNOWPLOW_APP_ID,
    environment: env.NEXT_PUBLIC_SNOWPLOW_ENV,
    deviceType,
    isInApp: isInZeloolAppFlag,
    gzipEnabled: env.NEXT_PUBLIC_ENABLE_SNOWPLOW_GZIP === 'true',
    adapters: createZeeloolSnowplowDataAdapters(),
  });
  const snowplowClient = configureZeeloolSnowplowClient(snowplowSdkConfig);
  const snowplowProvider = new SnowplowSdkProvider(snowplowClient, snowplowSdkConfig.enabled);
  center.registerProvider(snowplowProvider);
  // 先同步导入全部配置前快照，再允许首次身份绑定与渠道初始化。
  configureBrowserAnalytics({
    client: snowplowClient,
    sensors: sensorsProvider,
    snowplow: snowplowProvider,
  });

  // Initialize all providers (only enabled ones will actually initialize)
  await center.initialize();

  return center;
}
